import { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality, FunctionDeclaration, Type } from '@google/genai';
import { getDynamicSystemInstruction } from '../constants';
import { AgentState } from '../types';
import { decode, decodeAudioData, createPcmBlob } from '../utils/audioUtils';
import { showToast } from '../utils/helpers';
import { API_CONFIG } from '../utils/apiConfig';

// Helper functions for product data extraction - MOVED TO MODULE LEVEL
const getStockInfo = (product: any, variant: string) => {
  if (!product) {
    return { status: 'Product information not available' };
  }

  
  const isDigital = product.is_digital === true || product.is_digital === 'true' || product.is_digital === 1;
  const isService = product.upload_type === 'service';
  
  // For digital products
  if (isDigital) {
    return {
      status: 'Available',
      type: 'Digital Product',
      availability: 'Instant download after purchase',
      notes: 'No physical inventory - unlimited digital copies available'
    };
  }
  
  // For services
  if (isService) {
    return {
      status: 'Available', 
      type: 'Service',
      availability: 'Bookable now',
      notes: 'Service appointments available'
    };
  }

  // For physical products with variations
  if (product.variations && product.variations.length > 0) {
    let variationStock = null;
    
    if (variant && variant !== 'default') {
      // Find specific variation
      variationStock = product.variations.find((v: any) => 
        v.variation_value.toLowerCase().includes(variant.toLowerCase())
      );
    }

    if (variationStock) {
      return {
        status: variationStock.inventory > 0 ? 'In Stock' : 'Out of Stock',
        variant: variationStock.variation_value,
        available_quantity: variationStock.inventory,
        price_adjustment: variationStock.price_adjustment > 0 ? `+₦${variationStock.price_adjustment}` : 'None'
      };
    } else {
      // Return all variations stock
      const variationsStock = product.variations.map((v: any) => ({
        variant: v.variation_value,
        status: v.inventory > 0 ? 'In Stock' : 'Out of Stock',
        quantity: v.inventory
      }));
      
      return {
        status: 'Multiple variants available',
        variants: variationsStock,
        total_in_stock: variationsStock.filter((v: any) => v.status === 'In Stock').length
      };
    }
  }

  // For physical products without variations
  const availableStock = (product.inventory || 0) - (product.total_sold || 0);
  return {
    status: availableStock > 0 ? 'In Stock' : 'Out of Stock',
    available_quantity: availableStock,
    total_sold: product.total_sold || 0,
    low_stock_alert: availableStock < 10 ? `Only ${availableStock} left!` : 'Good availability'
  };
};

const getDeliveryInfo = (product: any) => {
  if (!product) return { delivery: 'Information not available' };

  const isDigital = product.is_digital === true || product.is_digital === 'true' || product.is_digital === 1;
  
  if (isDigital) {
    return {
      delivery_type: 'Digital Download',
      timeline: 'Instant',
      features: [
        'Available immediately after payment',
        'Secure download with PIN protection',
        ...(product.allowed_downloads ? [`${product.allowed_downloads} download attempts`] : []),
        ...(product.download_expiry_days ? [`Download valid for ${product.download_expiry_days} days`] : [])
      ]
    };
  }

  try {
    const deliveryOptions = typeof product.delivery_options === 'string' 
      ? JSON.parse(product.delivery_options) 
      : product.delivery_options;

    if (!deliveryOptions) {
      return {
        delivery_type: 'Standard',
        timeline: product.delivery_timeline ? `${product.delivery_timeline} days` : 'Not specified',
        return_policy: product.return_policy === '0' ? 'No returns' : `${product.return_policy} days return policy`
      };
    }

    const options = [];
    if (deliveryOptions.pickup?.available) options.push('Store Pickup - Free');
    if (deliveryOptions.delivery?.available) {
      const deliveryInfo = ['Home Delivery'];
      if (deliveryOptions.delivery.fees?.lagos) deliveryInfo.push(`Lagos: ₦${deliveryOptions.delivery.fees.lagos}`);
      if (deliveryOptions.delivery.fees?.outside_lagos) deliveryInfo.push(`Outside Lagos: ₦${deliveryOptions.delivery.fees.outside_lagos}`);
      options.push(deliveryInfo.join(' - '));
    }

    return {
      available_options: options,
      timeline: product.delivery_timeline ? `${product.delivery_timeline} days` : 'Not specified',
      return_policy: product.return_policy === '0' ? 'No returns' : `${product.return_policy} days return policy`
    };
  } catch (error) {
    return {
      delivery_type: 'Standard',
      timeline: 'Contact store for details',
      notes: 'Delivery options may vary'
    };
  }
};

const getProductDetails = (product: any) => {
  if (!product) return { details: 'Product information not available' };

  const details: any = {
    name: product.product_name,
    brand: product.brand || 'Not specified',
    condition: product.product_condition || 'Not specified',
    description: product.description ? product.description.substring(0, 200) + '...' : 'No description available',
    sku: product.sku || 'N/A'
  };

  // Add digital product specifics
  if (product.is_digital) {
    details.type = 'Digital Product';
    if (product.digital_product_type) details.file_type = product.digital_product_type;
    if (product.file_size) details.file_size = product.file_size;
    if (product.file_type) details.format = product.file_type;
  } else {
    details.type = product.upload_type === 'service' ? 'Service' : 'Physical Product';
  }

  // Add variations if available
  if (product.variations && product.variations.length > 0) {
    details.available_variants = product.variations.map((v: any) => ({
      option: v.variation_value,
      in_stock: v.inventory > 0,
      price_adjustment: v.price_adjustment > 0 ? `+₦${v.price_adjustment}` : 'Standard price'
    }));
  }

  return details;
};

const getPricingInfo = (product: any, variant: string) => {
  if (!product) return { pricing: 'Information not available' };

  const basePrice = product.per_unit_cost || 0;
  let finalPrice = basePrice;
  let variantInfo = '';

  // Check for variant-specific pricing
  if (variant && variant !== 'default' && product.variations) {
    const variation = product.variations.find((v: any) => 
      v.variation_value.toLowerCase().includes(variant.toLowerCase())
    );
    if (variation) {
      finalPrice = basePrice + (variation.price_adjustment || 0);
      variantInfo = ` (${variation.variation_value})`;
    }
  }

  const pricing: any = {
    base_price: `₦${parseFloat(basePrice).toLocaleString()}`,
    final_price: `₦${parseFloat(finalPrice).toLocaleString()}${variantInfo}`,
    currency: product.currency || 'NGN'
  };

  // Check for negotiable pricing
  if (product.is_negotiable && product.starting_price && product.ending_price) {
    pricing.negotiable = true;
    pricing.min_acceptable = Number(product.starting_price);
    pricing.max_acceptable = Number(product.ending_price);
    pricing.notes = 'Price is negotiable';
  } else {
    pricing.negotiable = false;
    pricing.notes = 'Fixed price';
  }

  // Service-specific pricing
  if (product.upload_type === 'service' && product.upfront_payment_percentage) {
    pricing.service_booking = `${product.upfront_payment_percentage}% upfront payment required`;
  }

  return pricing;
};

const getCompleteProductInfo = (product: any, store: any, variant: string) => {
  if (!product) return { error: 'Product information not available' };

  return {
    product_overview: {
      name: product.product_name,
      store: store?.company_name || 'Unknown Store',
      type: product.is_digital ? 'Digital Product' : (product.upload_type === 'service' ? 'Service' : 'Physical Product'),
      status: 'Active'
    },
    pricing: getPricingInfo(product, variant),
    availability: getStockInfo(product, variant),
    delivery: getDeliveryInfo(product),
    specifications: getProductDetails(product)
  };
};

// Enhanced Tool Definition with actual product data
const checkStockTool: FunctionDeclaration = {
  name: 'checkStock',
  parameters: {
    type: Type.OBJECT,
    description: 'Check the inventory availability and product details.',
    properties: {
      variant: {
        type: Type.STRING,
        description: `The specific variant to check. For products with variations, specify the variation value (e.g., "Black 38", "Large", "Red"). 
        For products without variations, use "default" or leave empty for general stock information.`,
      },
      inquiry_type: {
        type: Type.STRING,
        description: `What specific information the user is asking about. Options: 
        - "stock_availability": Check if item is in stock and quantity available
        - "delivery_options": Check delivery methods and timelines
        - "product_details": Get specific product specifications
        - "pricing": Check current pricing, negotiable range, and whether the price is flexible
        - "all": Get comprehensive product overview`,
      }
    },
    required: ['inquiry_type'],
  },
};

const updatePriceTool: FunctionDeclaration = {
  name: 'updatePrice',
  description: 'Update the product price after a successful negotiation. ONLY use this when you and the customer have agreed on a specific price and they are ready to proceed.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      newPrice: {
        type: Type.NUMBER,
        description: 'The new agreed price for the product in NGN',
      }
    },
    required: ['newPrice'],
  },
};

const contactSellerTool: FunctionDeclaration = {
  name: 'contactSeller',
  description: 'LAST RESORT ONLY — Alert the seller after you have attempted negotiation yourself and reached an impasse, or when the customer explicitly asks for human assistance. Do NOT call this when the customer simply asks about price negotiation — negotiate with them directly first using checkStock and updatePrice.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      reason: {
        type: Type.STRING,
        description: 'The reason for contacting the seller (e.g., "Customer wants a bulk discount", "Customer has a special customization request")',
      }
    },
    required: ['reason'],
  },
};

const listProductsTool: FunctionDeclaration = {
  name: 'listProducts',
  description: 'Search or list the products available in the current store. Use this when a customer asks about other products, wants to browse the catalog, or is looking for something specific.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      search: {
        type: Type.STRING,
        description: 'Optional search term to filter products by name, brand, or category',
      },
      maxResults: {
        type: Type.NUMBER,
        description: 'Maximum number of products to return (default 10, max 20)',
      },
    },
  },
};

const male = 'Enceladus'

interface UseSalesAgentProps {
  company: any; // Replace with your specific company type
  // Add other configuration options as needed
}

export const useSalesAgent = (company?: UseSalesAgentProps['company'], onPriceAgreed?: (price: number) => void, onAgentTranscript?: (text: string) => void) => {
  const [state, setState] = useState<AgentState>(AgentState.DISCONNECTED);
  const [error, setError] = useState<string | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [userVolume, setUserVolume] = useState(0);
  const [aiVolume, setAiVolume] = useState(0);

  const sessionRef = useRef<any>(null);
  const inputContextRef = useRef<AudioContext | null>(null);
  const outputContextRef = useRef<AudioContext | null>(null);
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const outputNodeRef = useRef<GainNode | null>(null);
  const audioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const nextStartTimeRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const onPriceAgreedRef = useRef(onPriceAgreed);
  onPriceAgreedRef.current = onPriceAgreed;
  const onAgentTranscriptRef = useRef(onAgentTranscript);
  onAgentTranscriptRef.current = onAgentTranscript;

    const voice = male;

  // Cleanup function
  const disconnect = useCallback(async () => {
    if (state === AgentState.DISCONNECTED) return;

    console.log('Disconnecting...');
    
    // Stop mic stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Close audio contexts
    if (inputContextRef.current) {
      inputContextRef.current.close();
      inputContextRef.current = null;
    }
    if (outputContextRef.current) {
      outputContextRef.current.close();
      outputContextRef.current = null;
    }

    // Stop all playing sources
    audioSourcesRef.current.forEach(source => {
      try { source.stop(); } catch (e) {}
    });
    audioSourcesRef.current.clear();

    setState(AgentState.DISCONNECTED);
    setIsSpeaking(false);
    setUserVolume(0);
    setAiVolume(0);
  }, [state]);

  const sendTextMessage = useCallback(async (text: string) => {
    const sessionPromise = sessionRef.current;
    if (!sessionPromise) {
      showToast('Voice connection not active', 'error');
      return;
    }
    try {
      const session = await sessionPromise;
      session.sendClientContent({
        turns: [{ role: 'user' as const, parts: [{ text }] }],
        turnComplete: true,
      });
    } catch (err) {
      console.error('Failed to send text message:', err);
    }
  }, []);

  const connect = useCallback(async () => {
    if (!process.env.REACT_APP_GEMINI_API_KEY) {
      setError("API Key not found.");
      return;
    }

    setState(AgentState.CONNECTING);
    setError(null);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY });

      // Setup Audio Contexts
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const inputCtx = new AudioContext({ sampleRate: 16000 });
      const outputCtx = new AudioContext({ sampleRate: 24000 });
      
      inputContextRef.current = inputCtx;
      outputContextRef.current = outputCtx;

      // Setup Output Node (Speakers)
      const outputNode = outputCtx.createGain();
      outputNode.connect(outputCtx.destination);
      outputNodeRef.current = outputNode;

      // Get Microphone Stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-09-2025',
        config: {
          responseModalities: [Modality.AUDIO],
          systemInstruction: getDynamicSystemInstruction(),
          tools: [{ functionDeclarations: [checkStockTool, updatePriceTool, contactSellerTool, listProductsTool] }],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: voice } },
          },
        },
        callbacks: {
          onopen: () => {
            console.log('Session opened');
            setState(AgentState.CONNECTED);

            // Setup Input Processing (Mic -> Gemini)
            const source = inputCtx.createMediaStreamSource(stream);
            inputSourceRef.current = source;
            
            // Use ScriptProcessor for capturing raw PCM
            const processor = inputCtx.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;

            processor.onaudioprocess = (e) => {
              const inputData = e.inputBuffer.getChannelData(0);
              
              // Simple volume calculation for visualization
              let sum = 0;
              for(let i=0; i<inputData.length; i++) sum += inputData[i] * inputData[i];
              const rms = Math.sqrt(sum / inputData.length);
              setUserVolume(Math.min(rms * 5, 1));

              const pcmBlob = createPcmBlob(inputData);
              sessionPromise.then(session => {
                session.sendRealtimeInput({ media: pcmBlob });
              });
            };

            source.connect(processor);
            processor.connect(inputCtx.destination);

            // Trigger Meo to greet the shopper first
            sessionPromise.then(session => {
              session.sendClientContent({
                turns: [{ role: 'user', parts: [{ text: 'Hi' }] }],
                turnComplete: true,
              });
            });
          },
          onmessage: async (message: LiveServerMessage) => {
            // 1. Handle Audio Output
            const base64Audio = message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
            if (base64Audio && outputContextRef.current && outputNodeRef.current) {
              const ctx = outputContextRef.current;
              
              setIsSpeaking(true);
              setAiVolume(0.5 + Math.random() * 0.5);

              // Sync Playback
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
              
              const audioBuffer = await decodeAudioData(
                decode(base64Audio),
                ctx,
                24000,
                1
              );

              const source = ctx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(outputNodeRef.current);
              
              source.addEventListener('ended', () => {
                audioSourcesRef.current.delete(source);
                if (audioSourcesRef.current.size === 0) {
                  setIsSpeaking(false);
                  setAiVolume(0);
                }
              });

              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += audioBuffer.duration;
              audioSourcesRef.current.add(source);
            }

            // Extract text from model response for chat display
            const textParts = message.serverContent?.modelTurn?.parts?.filter((p: any) => p.text);
            if (textParts?.length > 0) {
              const fullText = textParts.map((p: any) => p.text).join('');
              onAgentTranscriptRef.current?.(fullText);
            }

            // 2. Handle Interruption
            if (message.serverContent?.interrupted) {
              console.log('Interrupted!');
              audioSourcesRef.current.forEach(s => s.stop());
              audioSourcesRef.current.clear();
              nextStartTimeRef.current = 0;
              setIsSpeaking(false);
              setAiVolume(0);
            }

            // 3. Handle Tool Calls - ENHANCED VERSION
            if (message.toolCall) {
              for (const fc of message.toolCall.functionCalls) {
                if (fc.name === 'checkStock') {
                  // Get actual product data from the page
                  const productData = (window as any).__PRODUCT_DATA__;
                  
                  const args = fc.args as any;
                  const inquiryType = args.inquiry_type || 'stock_availability';
                  const variant = args.variant || 'default';

                 

                  console.log(`Checking ${inquiryType} for ${variant}`);

                  let result: any = {};

                  try {
                    switch (inquiryType) {
                      case 'stock_availability':
                        result = getStockInfo(productData, variant);
                        break;
                      case 'delivery_options':
                        result = getDeliveryInfo(productData);
                        break;
                      case 'product_details':
                        result = getProductDetails(productData);
                        break;
                      case 'pricing':
                        result = getPricingInfo(productData, variant);
                        break;
                      case 'all':
                        result = getCompleteProductInfo(productData, storeData, variant);
                        break;
                      default:
                        result = getStockInfo(productData, variant);
                    }
                  } catch (error) {
                    console.error('Error processing stock check:', error);
                    result = {
                      status: 'Error',
                      message: 'Unable to retrieve product information at this time.'
                    };
                  }
                  
                  sessionPromise.then(session => {
                    session.sendToolResponse({
                      functionResponses: {
                        id: fc.id,
                        name: fc.name,
                        response: { result },
                      }
                    });
                  });
                }

                if (fc.name === 'updatePrice') {
                  const productData = (window as any).__PRODUCT_DATA__;
                  const newPrice = Number(fc.args.newPrice);
                  const productId = productData?.id || productData?.product_id;

                  // Validate: price must be positive
                  if (!newPrice || newPrice <= 0) {
                    sessionPromise.then(session => {
                      session.sendToolResponse({
                        functionResponses: {
                          id: fc.id,
                          name: fc.name,
                          response: { result: { status: 'error', message: 'Invalid price. Please provide a valid positive amount.' } },
                        }
                      });
                    });
                    return;
                  }

                  // Validate against negotiation range if applicable
                  const isNegotiable = productData?.is_negotiable;
                  const minPrice = Number(productData?.starting_price || 0);
                  const maxPrice = Number(productData?.ending_price || 0);

                  if (isNegotiable && minPrice > 0 && maxPrice > 0) {
                    if (newPrice < minPrice) {
                      sessionPromise.then(session => {
                        session.sendToolResponse({
                          functionResponses: {
                            id: fc.id,
                            name: fc.name,
                            response: { result: { status: 'error', message: `The minimum acceptable price is ₦${minPrice.toLocaleString()}. Please negotiate within the allowed range.` } },
                          }
                        });
                      });
                      return;
                    }
                    if (newPrice > maxPrice) {
                      sessionPromise.then(session => {
                        session.sendToolResponse({
                          functionResponses: {
                            id: fc.id,
                            name: fc.name,
                            response: { result: { status: 'error', message: `The maximum price is ₦${maxPrice.toLocaleString()}. The price cannot exceed this.` } },
                          }
                        });
                      });
                      return;
                    }
                  }

                  // Notify the UI so it can show the agreed price alongside the original
                  onPriceAgreedRef.current?.(newPrice);
                  showToast(`Price agreed at ₦${newPrice.toLocaleString()}`, 'success');

                  sessionPromise.then(session => {
                    session.sendToolResponse({
                      functionResponses: {
                        id: fc.id,
                        name: fc.name,
                        response: { result: { status: 'success', newPrice, message: `Price has been updated to ₦${newPrice.toLocaleString()}` } },
                      }
                    });
                  });
                }

                if (fc.name === 'contactSeller') {
                  const reason = String(fc.args.reason || 'Customer requested seller assistance');
                  showToast(`Seller notified: ${reason}`, 'info');

                  sessionPromise.then(session => {
                    session.sendToolResponse({
                      functionResponses: {
                        id: fc.id,
                        name: fc.name,
                        response: { result: { status: 'notified', message: 'The seller has been notified and will get back to you shortly.' } },
                      }
                    });
                  });
                }

                if (fc.name === 'listProducts') {
                  const search = fc.args?.search || '';
                  const maxResults = fc.args?.maxResults || 200;
                  const storeData = (window as any).__COMPANY_DATA__;
                  const companyId = storeData?.username || storeData?.company_id;

                  if (!companyId) {
                    sessionPromise.then(session => {
                      session.sendToolResponse({
                        functionResponses: {
                          id: fc.id,
                          name: fc.name,
                          response: { result: { error: 'Store information not available' } },
                        }
                      });
                    });
                  } else {
                    // Try cached products first
                    let products = (window as any).__STORE_PRODUCTS__;
                    if (!products) {
                      try {
                        const response = await fetch(
                          `${API_CONFIG.BUSINESS_BACKEND_URL}${API_CONFIG.STORE.STORE_V3}/${companyId}?page=1&limit=200`
                        );
                        const data = await response.json();
                        products = data.products || data.data || [];
                        (window as any).__STORE_PRODUCTS__ = products;
                      } catch (err) {
                        console.error('Failed to fetch store products:', err);
                        sessionPromise.then(session => {
                          session.sendToolResponse({
                            functionResponses: {
                              id: fc.id,
                              name: fc.name,
                              response: { result: { error: 'Unable to fetch product catalog at this time' } },
                            }
                          });
                        });
                        return;
                      }
                    }

                    const mapped = products.map((p: any) => ({
                      name: p.product_name,
                      price: p.per_unit_cost,
                      currency: p.currency || 'NGN',
                      inStock: (p.inventory || 0) - (p.total_sold || 0) > 0,
                      negotiable: !!(p.is_negotiable && p.starting_price && p.ending_price),
                      slug: p.slug || p.id,
                      brand: p.brand || '',
                      category: p.category || '',
                    }));

                    const result = search
                      ? mapped.filter((p: any) =>
                          p.name.toLowerCase().includes(search.toLowerCase()) ||
                          p.brand.toLowerCase().includes(search.toLowerCase()) ||
                          p.category.toLowerCase().includes(search.toLowerCase())
                        )
                      : mapped;

                    sessionPromise.then(session => {
                      session.sendToolResponse({
                        functionResponses: {
                          id: fc.id,
                          name: fc.name,
                          response: { result: { products: result, total: result.length } },
                        }
                      });
                    });
                  }
                }
              }
            }
          },
          onclose: () => {
            console.log('Session closed');
            setState(AgentState.DISCONNECTED);
          },
          onerror: (e) => {
            console.error('Session error', e);
            setError("Connection error occurred.");
            disconnect();
          }
        }
      });

      sessionRef.current = sessionPromise;

    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to connect");
      setState(AgentState.ERROR);
    }
  }, []);

  return {
    state,
    connect,
    disconnect,
    sendTextMessage,
    isSpeaking,
    userVolume,
    aiVolume,
    error
  };
};